import { Card, FormControlLabel, IconButton, Switch, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFormikContext } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox/index";
import MDTypography from "@/components/atoms/MDTypography";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import { ProductFormMessages } from "@/messages/products/product-form.messages";

type Props = {
  messages: ProductFormMessages;
};

const AdditionalInfo = React.forwardRef<HTMLDivElement, Props>(function AdditionalInfo(
  { messages },
  ref
) {
  const { values, handleChange, errors, setFieldValue } = useFormikContext<ProductFormData>();

  const hasVariations = (values.variations?.length ?? 0) > 0;

  React.useEffect(() => {
    if (!hasVariations && values.groupVariations) {
      setFieldValue("groupVariations", false);
    }
  }, [hasVariations, values.groupVariations, setFieldValue]);

  return (
    <Card ref={ref} sx={{ width: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight={"bold"}>
            {messages.additionalInformationTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                variant="standard"
                name="warrantyTime"
                label={messages.warrantyTimeLabel}
                value={values.warrantyTime ?? ""}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.warrantyTime}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                variant="standard"
                name="ncm"
                label={messages.ncmLabel}
                value={values.ncm}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.ncm}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="pickUpInStore"
                    checked={!!values.pickUpInStore}
                    onChange={handleChange}
                  />
                }
                label={messages.pickUpInStoreLabel}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="needComment"
                    checked={!!values.needComment}
                    onChange={handleChange}
                  />
                }
                label={messages.needCommentLabel}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="groupVariations"
                    checked={hasVariations && !!values.groupVariations}
                    onChange={handleChange}
                    disabled={!hasVariations}
                  />
                }
                label={
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    {messages.groupVariationsLabel}
                    <Tooltip
                      title={
                        hasVariations
                          ? messages.groupVariationsTooltip
                          : messages.groupVariationsDisabledTooltip
                      }
                      arrow
                    >
                      <IconButton size="small" sx={{ padding: 0, minWidth: "auto" }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="useShortDescriptionInSales"
                    checked={!!values.useShortDescriptionInSales}
                    onChange={handleChange}
                  />
                }
                label={
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    {messages.useShortDescriptionInSalesLabel}
                    <Tooltip title={messages.useShortDescriptionInSalesTooltip} arrow>
                      <IconButton size="small" sx={{ padding: 0, minWidth: "auto" }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                }
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
});

export default AdditionalInfo;
